import React, { FC, useState } from 'react';
import {
  BreakpointConfig,
  createBreakpoints,
  Picture,
} from '../TemplatesGrid/components/Template/Picture/Picture';
import { useTranslation } from '@wix/wix-i18n-config';
import cs from 'classnames';
import { templateImageDataHooks } from './TemplateImage.dataHooks';
import { useBiLogger } from '../../contexts/BILoggerContext';
import { isBlankTemplate, Template } from '../../domain/entities/template';
import { useViewUrl } from '../../hooks/useViewUrl';
import { useIsMounted } from '../../hooks/useIsMounted';
import { useTimeout } from '../../hooks/useTimeout';
import { EditButton } from '../EditButton/EditButton';
import { ViewButton } from '../ViewButton/ViewButton';

import s from './TemplateImage.scss';
import { usePreviewTemplate } from '../../contexts/PreviewTemplateContext';
import { useExperiments } from '../../contexts/ExperimentsContext';
import { useInitialState } from '../../contexts/InitialStateContext';

export interface TemplateImageProps {
  isLazyLoad?: boolean;
  className?: string;
  template: Template;
  index?: number;
}

const SOURCE_IMAGE_SIZE = { width: 1920, height: 2222 };
export const BREAKPOINTS_CONFIG: BreakpointConfig[] = [
  {
    maxScreenWidth: 375,
    imageWidth: 295,
    densityList: [1, 2],
  },
  {
    maxScreenWidth: 833,
    imageWidth: 751,
    densityList: [1, 2],
  },
  {
    maxScreenWidth: 1366,
    imageWidth: 401,
    densityList: [1, 2],
  },
  {
    maxScreenWidth: 1440,
    imageWidth: 438.5,
    densityList: [1, 2],
  },
  {
    maxScreenWidth: 1920,
    imageWidth: 425.33,
    densityList: [1, 2],
  },
  {
    maxScreenWidth: 2560,
    imageWidth: 638.66,
    densityList: [1, 2],
  },
];

export const TemplateImage: FC<TemplateImageProps> = ({
  template,
  isLazyLoad,
  className,
  index,
}) => {
  const breakpoints = createBreakpoints(BREAKPOINTS_CONFIG, SOURCE_IMAGE_SIZE);
  const { t } = useTranslation();
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [isImageDelayLoading, setIsImageDelayLoading] = useState(false);
  const viewUrl = useViewUrl(template);
  const isMounted = useIsMounted();
  const biLogger = useBiLogger();
  const { isMobile } = useInitialState();
  const experiments = useExperiments();
  const [, setPreviewTemplate] = usePreviewTemplate();
  const thumbnail = template.assets?.find(
    (asset) => asset.type === 'RESPONSIVE_TALL',
  );
  const timeoutDelay = 300;

  const timeout = useTimeout();

  const isPreviewTemplateExperiment = experiments.is(
    'specs.funnel.StudioTemplatesPreviewTemplateMobile',
    'true',
  );

  const isStudioPreviewInMobile = isMobile && isPreviewTemplateExperiment;

  const onImageLoadStart = () => {
    setIsImageLoading(true);
    setIsImageDelayLoading(true);
    timeout.set(() => {
      timeout.clear();
      setIsImageDelayLoading(false);
    }, timeoutDelay);
  };

  const onViewClick = () => {
    biLogger.reportViewTemplate({ ...template, index });
  };
  const onViewTemplateClick = () => {
    if (isStudioPreviewInMobile) {
      setPreviewTemplate(template);
    }
  };

  return (
    <div onClick={onViewTemplateClick} className={s.templateImageContainer}>
      {thumbnail?.absoluteUrl ? (
        <>
          <div
            className={cs(s.templateImageLoadingContainer, {
              [s.animationOverImage]: isMounted,
              [s.loading]: isImageLoading || isImageDelayLoading,
            })}
          />
          <Picture
            isLazyLoad={isLazyLoad}
            imagePath={thumbnail?.absoluteUrl || ''}
            sourceImageSize={SOURCE_IMAGE_SIZE}
            breakpoints={breakpoints}
            alt={t('templates.img.altText', template.title)}
            onLoad={() => setIsImageLoading(false)}
            onLoadStart={onImageLoadStart}
            imageClassName={className}
            dataHook={templateImageDataHooks.image({
              key: template.templateId,
            })}
          />
        </>
      ) : (
        <div className={s.templateImageFallback} />
      )}
      {!isStudioPreviewInMobile && (
        <a
          target="_blank"
          className={cs(s.viewLink, s.hideOnDesktop)}
          href={viewUrl}
          rel="noreferrer"
          onClick={onViewClick}
        >
          {t('app.templates.grid.view.button')}
        </a>
      )}
      <div className={s.buttonsOverlay}>
        <div className={s.buttonsContainer}>
          <EditButton
            skin="light"
            template={template}
            index={index}
            dataHook={templateImageDataHooks.editButton({
              key: template.templateId,
            })}
            className={s.editButton}
          />
          {!isBlankTemplate(template) && (
            <ViewButton
              skin="light"
              template={template}
              index={index}
              dataHook={templateImageDataHooks.viewButton({
                key: template.templateId,
              })}
              className={s.viewButton}
            />
          )}
        </div>
      </div>
    </div>
  );
};
